import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast } from "@jsq/ds/core";
import { FormTextInput, FormSelect, SubmitButton } from "@jsq/ds/forms";
import { useSetBankAccountVerificationStatusMutation } from "~/graphql/supergraphHooks";
import {
  type SetBankAccountVerificationStatusMutation,
  type SetBankAccountVerificationStatusInput,
  type BankAccountVerificationStatus,
} from "~/graphql/supergraphTypes";
import { getErrorMessage } from "~/utils/errors";
import { toDbId } from "~/utils/graphql-helpers";
import * as styles from "./styles_vanilla.css";

// Types
type BankAccountObjectType =
  SetBankAccountVerificationStatusMutation["setBankAccountVerificationStatus"];

interface FormValues {
  bankAccountId: string;
  verificationStatus: BankAccountVerificationStatus;
}

const validationSchema = Yup.object().shape({
  bankAccountId: Yup.string().required("Bank Account ID is required"),
  verificationStatus: Yup.string().required("Verification Status is required"),
});

const initialValues: FormValues = {
  bankAccountId: "",
  verificationStatus: "UNVERIFIED",
};

const verificationStatusOptions = [
  { value: "UNVERIFIED" as BankAccountVerificationStatus, label: "Unverified" },
  { value: "VERIFIED" as BankAccountVerificationStatus, label: "Verified" },
  { value: "TERMINAL_ERROR" as BankAccountVerificationStatus, label: "Terminal Error" },
];

const SetBankAccountVerificationStatus: React.FC = () => {
  const [submittedBankAccount, setSubmittedBankAccount] =
    useState<BankAccountObjectType | null>(null);

  const [setBankAccountVerificationStatus, { loading }] =
    useSetBankAccountVerificationStatusMutation({
      onCompleted: (data) => {
        if (data?.setBankAccountVerificationStatus) {
          setSubmittedBankAccount(data.setBankAccountVerificationStatus);
          toast.success("Bank account verification status updated successfully");
        }
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
      },
    });

  const handleSubmit = async (values: FormValues) => {
    const input: SetBankAccountVerificationStatusInput = {
      bankAccountId: parseInt(values.bankAccountId, 10),
      verificationStatus: values.verificationStatus,
    };

    await setBankAccountVerificationStatus({
      variables: { setBankAccountVerificationStatusInput: input },
    });
  };

  return (
    <Flex
      data-testid="set-bank-account-verification-status-root"
      flexDirection="column"
      className={styles.container}
    >
      <div className={styles.header}>
        <Text variant="regular-xxxl" className={styles.pageTitle}>
          Set Bank Account Verification Status
        </Text>
      </div>
      <div
        className={styles.contentArea}
        data-testid="set-bank-account-verification-status-content"
      >
        <div className={styles.formSection}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <div className={styles.formFields}>
                <FormTextInput
                  name="bankAccountId"
                  label="Bank Account ID"
                  placeholder="Enter bank account ID"
                  required
                />
                <FormSelect
                  name="verificationStatus"
                  label="Verification Status"
                  placeholder="Select verification status"
                  required
                  full
                >
                  <FormSelect.Section>
                    {verificationStatusOptions.map((option) => (
                      <FormSelect.Option key={option.value} value={option.value}>
                        {option.label}
                      </FormSelect.Option>
                    ))}
                  </FormSelect.Section>
                </FormSelect>
              </div>
              <div className={styles.submitSection}>
                <SubmitButton disabled={loading}>
                  Set Bank Account Verification Status
                </SubmitButton>
              </div>
            </Form>
          </Formik>
        </div>
        {submittedBankAccount && (
          <div className={styles.resultSection}>
            <Text variant="regular-xl" className={styles.resultTitle}>
              Success
            </Text>
            <div className={styles.resultDetails}>
              <Text variant="regular-md">
                <strong>Bank Account ID:</strong>{" "}
                {submittedBankAccount?.id ? toDbId(submittedBankAccount.id) : "N/A"}
              </Text>
              <Text variant="regular-md">
                <strong>Verification Status:</strong>{" "}
                {submittedBankAccount?.verificationStatus || "N/A"}
              </Text>
            </div>
          </div>
        )}
      </div>
    </Flex>
  );
};

export default SetBankAccountVerificationStatus;
export { SetBankAccountVerificationStatus };
